import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { useTheme } from '@mui/material/styles';
import AutoLockSubsection from 'app/pages/loanApplication/AutoLockSubsection';
import {
  AutoLockFormsLevelType,
  UnderwritingSubsectionNames,
} from 'app/services/autoLockForms/autoLockForms.constants';
import clsx from 'clsx';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { FeatureFlag } from 'constants/featureFlags';
import { UnderwritingSubtabRouteParam } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  getApplicationBorrowersAction,
  getUnderwritingDataAction,
} from 'store/loanApplications/actions';
import { selectSuspenseReason } from 'store/loanApplications/selectors';

import { getSharedAccordionStyles } from '../../LoanApplication.sharedStyles';

import {
  LoanBundleHistoryAndConsent,
  LoanDenial,
  Notes,
  QualificationsDetails,
  SuspenseReason,
  UWReports,
} from './tabs';

const UnderwritingTab = ({ setIsLoading }) => {
  const dispatch = useDispatch();

  const theme = useTheme();

  const { applicationId, subtab } = useParams();
  const loanId = useLoanId();

  const sharedClasses = useMemo(() => getSharedAccordionStyles(theme), [theme]);

  const { isManageAllowed: isUnderwritingAccessAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_UNDERWRITING),
  );
  const { isViewAllowed: isReportsAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.UNDERWRITING_REPORTS),
  );

  const { isViewAllowed: isLoanDenialVisible } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_DENIAL),
  );
  const suspenseReason = useSelector(selectSuspenseReason);

  const isQualificationsDetailsAllowed =
    useFeatureToggle(FeatureFlag.EnableQualificationDetails) && isUnderwritingAccessAllowed;

  const reportsRef = useRef(null);

  useEffect(() => {
    if (subtab !== UnderwritingSubtabRouteParam.UW_REPORTS) {
      return;
    }

    reportsRef.current?.scrollIntoView({
      block: 'start',
    });
  }, [subtab]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      loanId && dispatch(getUnderwritingDataAction({ loanId })),
      dispatch(getApplicationBorrowersAction({ applicationId })),
    ]).finally(() => setIsLoading(false));
  }, [dispatch, loanId, applicationId, setIsLoading]);

  return (
    <div className={clsx(sharedClasses.root)}>
      {isQualificationsDetailsAllowed && (
        <AutoLockSubsection
          areaName={UnderwritingSubsectionNames.GENERAL}
          levelType={AutoLockFormsLevelType.SUBSECTION}
        >
          <QualificationsDetails />
        </AutoLockSubsection>
      )}
      {isUnderwritingAccessAllowed && (
        <AutoLockSubsection
          areaName={UnderwritingSubsectionNames.NOTES}
          levelType={AutoLockFormsLevelType.SUBSECTION}
        >
          <Notes />
        </AutoLockSubsection>
      )}
      <LoanBundleHistoryAndConsent />
      <div ref={reportsRef}>{isReportsAllowed && <UWReports />}</div>
      {isUnderwritingAccessAllowed && isLoanDenialVisible && (
        <AutoLockSubsection
          areaName={UnderwritingSubsectionNames.DENIAL_REASONS}
          levelType={AutoLockFormsLevelType.SUBSECTION}
        >
          <LoanDenial />
        </AutoLockSubsection>
      )}
      {isUnderwritingAccessAllowed && !!suspenseReason && <SuspenseReason />}
    </div>
  );
};

UnderwritingTab.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(UnderwritingTab);
